import type { UseQueryResult } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import type { OptionType } from '@innovamat/glimmer-components';
import type { ClassroomsQuery, Maybe } from '@innovamat/glow-api-client';
import { useClassroomsQuery } from '@innovamat/glow-api-client';
import { stages } from '@innovamat/radiance-utils';

import { useUser } from '../../user-management';
import { classroomsMapper } from '../apis/mappers';
import type { Classroom, ClassroomType } from '../modules/classroom';
import { isGoogleClassroom as checkIsGoogleClassroom } from '../modules/classroom';
import { filterAndSortClassrooms } from '../modules/classroom/classrooms';
import { usePermissionCourses } from './use-permission-courses';

type Props = {
  enabled?: boolean;
  mine: boolean;
  organizationId?: string;
  type?: ClassroomType;
};

type UseClassrooms = {
  classrooms: Classroom[];
  classroomExists: (id: Maybe<string> | undefined) => boolean | undefined;
  classroomsByStage: (
    order: number,
    filterGoogleClassrooms?: boolean
  ) => Classroom[];
  classroomsQuery: UseQueryResult<ClassroomsQuery, unknown>;
  classroomToOption: (classroom: Classroom) => OptionType;
  isError: boolean;
  isGoogleClassroom: (id: Maybe<string> | undefined) => boolean;
  isLoading: boolean;
  isSuccess: boolean;
  filterClassroomByDigitalPractice: (classroom: Classroom) => boolean;
};

const toOption = (classroom: Classroom): OptionType => ({
  label: `${String(classroom?.courseName)} - ${String(classroom?.name)}`,
  value: String(classroom?.id),
});

function useClassrooms({
  enabled,
  mine,
  organizationId,
  type,
}: Props): UseClassrooms {
  const { user, isRole } = useUser();
  const orgId = organizationId || user?.organizationId!;
  const { allPermissions } = usePermissionCourses({});

  const classroomsQuery = useClassroomsQuery(
    {
      mine,
      orgId: orgId,
    },
    {
      enabled:
        enabled === undefined || enabled === null
          ? !!orgId && !isRole.Student
          : enabled,
      staleTime: Infinity,
    }
  );

  const classrooms = useMemo(() => {
    if (!classroomsQuery.data?.classrooms) {
      return [];
    }

    const filteredClassrooms = filterAndSortClassrooms(
      classroomsQuery.data.classrooms as Classroom[],
      type
    );

    return classroomsMapper(filteredClassrooms);
  }, [classroomsQuery.data?.classrooms, type]);

  const classroomExists = useCallback(
    (id: Maybe<string> | undefined): boolean =>
      classrooms.some((classroom) => classroom?.id === id),
    [classrooms]
  );

  const classroomsByStage = (
    order: number,
    filterGoogleClassrooms?: boolean
  ): Classroom[] => {
    const isSecundariaCourse = stages.isSecundaria(order);
    const classroomsFiltered =
      classrooms?.filter((c: Classroom) =>
        isSecundariaCourse
          ? stages.isSecundaria(c?.courseOrder)
          : !stages.isSecundaria(c?.courseOrder)
      ) || [];

    return filterGoogleClassrooms
      ? classroomsFiltered?.filter(
          (classroom) => !checkIsGoogleClassroom(classroom)
        )
      : classroomsFiltered;
  };

  const classroomToOption = (classroom: Classroom): OptionType =>
    toOption(classroom);

  const isGoogleClassroom = useCallback(
    (id: Maybe<string> | undefined): boolean => {
      if (!classroomExists(id)) {
        return false;
      }

      const classroom = classrooms.find((classroom) => classroom?.id === id);
      return classroom ? checkIsGoogleClassroom(classroom) : false;
    },
    [classroomExists, classrooms]
  );

  const filterClassroomByDigitalPractice = (classroom: Classroom): boolean => {
    const coursePermission = allPermissions?.find(
      (permission) => permission?.courseId === classroom.courseId
    );
    return !!coursePermission?.permission?.platformEnabled;
  };

  return {
    classrooms,
    classroomExists,
    classroomsByStage,
    classroomsQuery,
    classroomToOption,
    isError: classroomsQuery.isError,
    isLoading: classroomsQuery.isLoading,
    isSuccess: classroomsQuery.isSuccess,
    isGoogleClassroom,
    filterClassroomByDigitalPractice,
  };
}

export { useClassrooms };
